import * as React from "react";
import { useEffect, useId, useState } from "react";
import { Dialog } from "@mui/material";
import { WindowChrome } from "./WindowChrome";
import { useMinimizedDock } from "./MinimizedDock";
import "./BrandWindow.scss";

export type BrandModalProps = React.PropsWithChildren<{
  open: boolean;
  onClose: () => void;
  title: string;
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
  fullWidth?: boolean;
  onTitleClick?: () => void;
}>;

/**
 * A dialog dressed as a brand OS window: banana title bar with minimize
 * (→ dock chip), fullscreen, and close controls.
 */
export const BrandModal: React.FC<BrandModalProps> = ({
  open,
  onClose,
  title,
  maxWidth = "sm",
  fullWidth = true,
  onTitleClick,
  children,
}) => {
  const id = useId();
  const [minimized, setMinimized] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const dock = useMinimizedDock();

  // Reset window state whenever it's (re)opened or closed
  useEffect(() => {
    if (!open) {
      setMinimized(false);
      setFullscreen(false);
    }
  }, [open]);

  useEffect(() => {
    if (open && minimized) {
      dock.add({ id, title, restore: () => setMinimized(false) });
      return () => dock.remove(id);
    }
    dock.remove(id);
  }, [open, minimized, id, title, dock]);

  const handleClose = () => {
    setMinimized(false);
    setFullscreen(false);
    onClose();
  };

  return (
    <Dialog
      open={open && !minimized}
      onClose={handleClose}
      fullScreen={fullscreen}
      fullWidth={fullWidth}
      maxWidth={maxWidth}
    >
      <WindowChrome
        title={title}
        fullscreen={fullscreen}
        onTitleClick={onTitleClick}
        onMinimize={() => setMinimized(true)}
        onToggleFullscreen={() => setFullscreen((f) => !f)}
        onClose={handleClose}
      />
      {children}
    </Dialog>
  );
};
